// SPDX-License-Identifier: MIT

// Turns the declarative `trackers` specs on each consent type (plain data from
// gatsby-config, e.g. { type: 'facebookPixel', id: '123' }) into real
// onAccept/onReject/onRouteChange callbacks, using the built-in factories. This
// is what lets the whole config live in the plugin's gatsby-config options —
// tracker specs serialize (unlike function callbacks).

const {
  facebookPixel,
  redditPixel,
  pinterestPixel,
  linkedInInsight,
  microsoftUet,
  tiktokPixel,
  twitterPixel,
  snapchatPixel,
  intercom,
  googleTagManager,
  combineTrackers,
} = require('./trackers')
const { DEFAULT_CONSENT_TYPE_TEXT } = require('./defaults')
const { getConsentState } = require('./consentState')

const FACTORIES = {
  facebookPixel,
  redditPixel,
  pinterestPixel,
  linkedInInsight,
  microsoftUet,
  tiktokPixel,
  twitterPixel,
  snapchatPixel,
  intercom,
  googleTagManager,
}

const buildTracker = (specs = [], hasGtm = false) => {
  const built = specs
    .map((spec) => {
      const factory = FACTORIES[spec.type]
      if (!factory) {
        // eslint-disable-next-line no-console
        console.warn(`gatsby-plugin-cookie-consent: unknown tracker type "${spec.type}"`)
        return null
      }
      // Pixels default to fallback mode when a GTM container is declared (GTM may
      // already fire the same pixel), and to immediate loading otherwise — inferred
      // here so it needs no per-pixel flag. An explicit `fallback` on the spec still
      // wins. Non-pixel factories ignore it. `fallbackDelayMs` is carried through.
      const resolvedSpec =
        spec.fallback === undefined ? { ...spec, fallback: hasGtm } : spec
      // Pass id (the common case) plus the spec, so a tracker that needs more than
      // an id (e.g. googleTagManager's defaultDataLayer) can read it.
      return factory(spec.id, resolvedSpec)
    })
    .filter(Boolean)
  return combineTrackers(built)
}

// True if any consent type declares a googleTagManager container.
const configHasGtm = (consentTypes) =>
  consentTypes.some((t) =>
    (t.trackers || []).some((s) => s.type === 'googleTagManager')
  )

// Returns { consentTypes, onRouteChange }. For each type: fills in the default
// label/description for its id (unless provided), and builds onAccept/onReject
// from its declarative `trackers`. Also returns a combined route-change handler
// (fires page views on SPA navigation; each is a no-op until consented).
const resolveConsentTypes = (consentTypes = []) => {
  const hasGtm = configHasGtm(consentTypes)
  const routeHandlers = []
  const resolved = consentTypes.map((type) => {
    const defaults = DEFAULT_CONSENT_TYPE_TEXT[type.id] || {}
    const { trackers, ...rest } = type
    const resolvedType = {
      ...rest,
      label: type.label || defaults.label || type.id,
      description: type.description || defaults.description || '',
    }
    if (!trackers || trackers.length === 0) return resolvedType
    const tracker = buildTracker(trackers, hasGtm)
    routeHandlers.push({ id: type.id, required: !!type.required, tracker })
    resolvedType.onAccept = tracker.onAccept
    resolvedType.onReject = tracker.onReject
    return resolvedType
  })
  // Only fire route-change page views for categories that are currently granted,
  // so a category the visitor has since rejected doesn't keep tracking on
  // navigation (e.g. Intercom re-activating via 'update' after shutdown).
  const onRouteChange = () => {
    const state = getConsentState()
    routeHandlers.forEach((h) => {
      if (h.required || state[h.id]) h.tracker.onRouteChange()
    })
  }
  return { consentTypes: resolved, onRouteChange }
}

module.exports = { resolveConsentTypes }
